import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { interpolateColor } from "@utils/chart-utils";
import { RootState } from "..";
import { QuestionResponsesSearchFilter } from "@/models/Filter";
import type {
  TimePeriod,
  TimePeriodSelection,
  StatTestType,
  SignificanceLevel,
} from "./chart-config-slice";
import type { RollingAverageConfig } from "@/utils/rolling-average-utils";
import {
  type PointInTimeViewerOptions,
  DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
} from "@models/Dataset";
import {
  DEFAULT_VISUALIZATION_BASE,
  type VisualizationBase,
} from "@models/VisualizationBase";
import type { WidgetRollup } from "@models/Rollup";

export type { TimePeriod, TimePeriodSelection, StatTestType, SignificanceLevel };

// Reuses the Bar Chart shape, minus compareToTotal (a crosstab-only concept).
export interface DonutStatisticalTestingConfig {
  enabled: boolean;
  testType: StatTestType;
  significanceLevel: SignificanceLevel;
  minimumSampleSize: number;
}

const DEFAULT_STATISTICAL_TESTING: DonutStatisticalTestingConfig = {
  enabled: false,
  testType: "t-test",
  significanceLevel: "90%",
  minimumSampleSize: 30,
};

const DEFAULT_ROLLING_AVERAGE: RollingAverageConfig = {
  enabled: false,
  windowSize: 2,
};

// Per-slice relabeling, Custom Order, and Pinned Start/End — the Bar
// Customization interaction model (PRD-41), without visibility/hide controls.
export interface DonutSliceCustomization {
  labelOverrides: Record<string, string>;
  customOrder: string[];
  pinnedStart: string | null;
  pinnedEnd: string | null;
}

const DEFAULT_SLICE_CUSTOMIZATION: DonutSliceCustomization = {
  labelOverrides: {},
  customOrder: [],
  pinnedStart: null,
  pinnedEnd: null,
};

export interface DonutChartConfig {
  // Chart basic settings
  title: string;
  subtitle: string;
  titleBold?: boolean;
  titleUnderline?: boolean;

  // Question metadata
  questionLabel?: string;
  questionName?: string;

  // Focus parameters for Single Grid questions (a Single question with
  // multiple variables). Donut only supports Variable focus — a grid
  // question's Values become the rendered slices for one chosen Variable.
  // Mirrors Bar Chart's focusMode/focusEntity shape, minus focus rollups
  // and Value focus, which Donut does not support.
  focusMode?: string; // "VARIABLE" (uppercase for API)
  focusEntity?: string; // variable.label (for API)
  focusEntityDisplayLabel?: string; // Display label shown in the picker (rowtitle/title)

  // Focus rollup — combines several grid Variables into one rollup slice,
  // mutually exclusive with focusEntity above.
  focusRollup?: WidgetRollup; // sent to API
  focusRollupName?: string; // display-only, configJson only

  // Weight and sorting
  weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT";
  dataSorting: "ascending" | "descending" | "none" | "custom";
  sliceCustomization: DonutSliceCustomization;

  // Donut-specific settings
  innerRadius: number; // 0-80% (0 = pie chart, >0 = donut)
  outerRadius: number; // 60-100%
  startAngle: number; // 0-360 degrees

  // Data Labels
  dataLabelsEnabled: boolean;
  dataLabelsPosition: "inside" | "outside" | "none";
  dataLabelsFormat: string; // "{point.name}: {point.percentage:.1f}%"
  dataLabelsDistance: number; // Distance from pie edge

  // Legend
  legendEnabled: boolean;

  // Interactivity
  allowPointSelect: boolean;
  sliceOffset: number; // Distance for selected slices

  // Center Content (for donut charts)
  centerText: string;
  centerTextEnabled: boolean;
  centerTextStyle: {
    fontSize: string;
    fontWeight: string;
    color: string;
  };

  // Center Value Configuration (Emphasis)
  emphasis: "none" | "highest" | string; // "none", "highest", or slice name
  emphasisDisplayFormat: "percentage_title" | "percentage_custom";
  emphasisCustomText: string;

  // Colors & Styling
  colorPalette: string;
  customColors: string[];
  // Per-slice manual color overrides, keyed by slice (or group) name.
  // Only applied when colorPalette === "Custom (per-slice)".
  manualColors: Record<string, string>;
  // Truncate long slice names in the legend and in "outside" data labels
  // (tooltips and accessible text always show the full name).
  truncateCategoryText: boolean;

  // Data formatting
  percentageFormatting: number;
  subOnePercentStyle: "<1%" | "0%";
  sliceValueFormat: "percentage" | "percentage_text";
  minSliceTextThreshold: number;
  forceShowAllValues: boolean;
  showTooltip: boolean;
  tooltipFormat: string;

  // N Display — unweighted respondent counts (PRD-41)
  showSliceN: boolean;
  showChartN: boolean;
  chartN?: number | null;

  // Data
  categories: string[];
  series: Array<{
    name: string;
    data: Array<{
      name: string;
      y: number;
      color?: string;
      isGroup?: boolean;
      groupId?: string;
      groupName?: string;
      baseN?: number | null;
      cellN?: number | null;
      significantDifference?: -1 | 0 | 1;
    }>;
  }>;

  // Slice Grouping
  groups: Array<{
    id: string;
    name: string;
    sliceNames: string[];
    showAsSlice: boolean;
    isHidden: boolean;
  }>;

  // Base — restricts the eligible respondent pool before slices are calculated.
  // Optional so older saved configs (pre-dating this field) keep loading.
  visualizationBase?: VisualizationBase;

  // Time Period — restricts the snapshot window slices are calculated over.
  timePeriodSelection: TimePeriodSelection;
  responsesMinDate?: string; // ISO date string (YYYY-MM-DD)
  responsesMaxDate?: string; // ISO date string (YYYY-MM-DD)
  viewerOptions: PointInTimeViewerOptions;

  // Rolling Average — pooled-window recalculation of slices/N/suppression/Stat Testing.
  rollingAverage: RollingAverageConfig;
  effectiveRollingPeriodCount?: number;
  rollingPeriodsWarningMessage?: string;
  comparisonRollingLabel?: string;
  missingRollingPeriodLabels?: string[];

  // Statistical Testing — compares each rendered slice/Rollup to the previous
  // equivalent period (or rolling window).
  statisticalTesting: DonutStatisticalTestingConfig;
}

interface Questions extends Record<string, any> {
  id: string;
  label: string;
  name: string;
  responses: number;
}

export interface DonutChartConfigState {
  currentConfig: DonutChartConfig;
  savedConfigs: DonutChartConfig[];
  previewMode: boolean;
  question: Questions | null;
  datasetId: string | null;
  datasetName: string | null;
  questionId: string | null;
  datasetVersionId: number | null;
  activeFilters: QuestionResponsesSearchFilter[];
  // Toggle to apply/ignore filters in preview
  applyFilters: boolean;
}

const defaultDonutChartConfig: DonutChartConfig = {
  title: "Market Share Analysis",
  subtitle: "",
  titleBold: false,
  titleUnderline: false,
  weight: "DISABLED",
  dataSorting: "none",
  sliceCustomization: { ...DEFAULT_SLICE_CUSTOMIZATION },
  innerRadius: 60, // Makes it a donut (60% inner radius)
  outerRadius: 80,
  startAngle: 0,
  dataLabelsEnabled: true,
  dataLabelsPosition: "inside",
  dataLabelsFormat: "{point.name}: {point.percentage:.1f}%", // This will be dynamically updated
  dataLabelsDistance: -45, // Fixed: should be -45 for "inside" position, not 15
  legendEnabled: true,
  allowPointSelect: true,
  sliceOffset: 10,
  centerText: "",
  centerTextEnabled: true,
  centerTextStyle: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#333333",
  },
  emphasis: "none",
  emphasisDisplayFormat: "percentage_title",
  emphasisCustomText: "",
  colorPalette: "Multicolor",
  customColors: [],
  manualColors: {},
  truncateCategoryText: false,
  percentageFormatting: 0,
  subOnePercentStyle: "<1%",
  sliceValueFormat: "percentage",
  minSliceTextThreshold: 2,
  forceShowAllValues: false,
  showTooltip: true,
  tooltipFormat: "{series.name}: <b>{point.percentage:.1f}%</b>", // This will be dynamically updated
  showSliceN: false,
  showChartN: false,
  categories: ["Chrome", "Safari", "Edge", "Firefox", "Other"],
  series: [
    {
      name: "Browser Share",
      data: [
        { name: "Chrome", y: 63.59, color: "#ffb09c" }, // orange
        { name: "Safari", y: 19.17, color: "#57dfbe" }, // green
        { name: "Edge", y: 4.36, color: "#757679" }, // black
        { name: "Firefox", y: 3.91, color: "#a1a7df" }, // purple
        { name: "Other", y: 8.97, color: "#c36179" }, // crimson
      ],
    },
  ],
  groups: [],
  visualizationBase: { ...DEFAULT_VISUALIZATION_BASE },
  timePeriodSelection: { timePeriod: "all_time", startDate: null, endDate: null },
  viewerOptions: { ...DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS },
  rollingAverage: { ...DEFAULT_ROLLING_AVERAGE },
  statisticalTesting: { ...DEFAULT_STATISTICAL_TESTING },
};

export const initialState: DonutChartConfigState = {
  currentConfig: { ...defaultDonutChartConfig },
  savedConfigs: [],
  previewMode: true,
  question: null,
  datasetId: null,
  datasetName: null,
  questionId: null,
  datasetVersionId: null,
  activeFilters: [],
  applyFilters: true,
};

export const donutChartConfigSlice = createSlice({
  name: "donutChartConfig",
  initialState,
  reducers: {
    togglePreviewMode: (state) => {
      state.previewMode = !state.previewMode;
    },
    updateChartTitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.title = action.payload;
    },
    updateChartSubtitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.subtitle = action.payload;
    },
    updateTitleBold: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleBold = action.payload;
    },
    updateTitleUnderline: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleUnderline = action.payload;
    },
    updateWeight: (
      state,
      action: PayloadAction<"DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT">,
    ) => {
      state.currentConfig.weight = action.payload;
    },
    updateDataSorting: (
      state,
      action: PayloadAction<"ascending" | "descending" | "none" | "custom">,
    ) => {
      state.currentConfig.dataSorting = action.payload;
    },
    updateSliceLabelOverride: (
      state,
      action: PayloadAction<{ sliceName: string; label: string }>,
    ) => {
      const { sliceName, label } = action.payload;
      const sc = state.currentConfig.sliceCustomization;
      if (label.trim() === "" || label === sliceName) {
        delete sc.labelOverrides[sliceName];
      } else {
        sc.labelOverrides[sliceName] = label;
      }
    },
    updateSlicePin: (
      state,
      action: PayloadAction<{
        sliceName: string;
        position: "none" | "start" | "end";
      }>,
    ) => {
      const { sliceName, position } = action.payload;
      const sc = state.currentConfig.sliceCustomization;
      // Clear this slice from any current pin position
      if (sc.pinnedStart === sliceName) sc.pinnedStart = null;
      if (sc.pinnedEnd === sliceName) sc.pinnedEnd = null;
      if (position === "start") {
        sc.pinnedStart = sliceName;
      } else if (position === "end") {
        sc.pinnedEnd = sliceName;
      }
    },
    updateSliceCustomOrder: (state, action: PayloadAction<string[]>) => {
      state.currentConfig.sliceCustomization.customOrder = action.payload;
    },
    updateInnerRadius: (state, action: PayloadAction<number>) => {
      state.currentConfig.innerRadius = action.payload;
    },
    updateOuterRadius: (state, action: PayloadAction<number>) => {
      state.currentConfig.outerRadius = action.payload;
    },
    updateStartAngle: (state, action: PayloadAction<number>) => {
      state.currentConfig.startAngle = action.payload;
    },
    updateDataLabelsEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.dataLabelsEnabled = action.payload;
    },
    updateDataLabelsPosition: (
      state,
      action: PayloadAction<"inside" | "outside" | "none">,
    ) => {
      state.currentConfig.dataLabelsPosition = action.payload;
      // Set distance based on position and enable/disable labels
      if (action.payload === "none") {
        state.currentConfig.dataLabelsEnabled = false;
        state.currentConfig.dataLabelsDistance = 0;
      } else if (action.payload === "inside") {
        state.currentConfig.dataLabelsEnabled = true;
        state.currentConfig.dataLabelsDistance = -45;
      } else if (action.payload === "outside") {
        state.currentConfig.dataLabelsEnabled = true;
        state.currentConfig.dataLabelsDistance = 15;
      }
    },
    updateDataLabelsFormat: (state, action: PayloadAction<string>) => {
      state.currentConfig.dataLabelsFormat = action.payload;
    },
    updateDataLabelsDistance: (state, action: PayloadAction<number>) => {
      state.currentConfig.dataLabelsDistance = action.payload;
    },
    updateLegendEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.legendEnabled = action.payload;
    },
    updateAllowPointSelect: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.allowPointSelect = action.payload;
    },
    updateSliceOffset: (state, action: PayloadAction<number>) => {
      state.currentConfig.sliceOffset = action.payload;
    },
    updateCenterText: (state, action: PayloadAction<string>) => {
      state.currentConfig.centerText = action.payload;
    },
    updateCenterTextEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.centerTextEnabled = action.payload;
    },
    updateCenterTextStyle: (
      state,
      action: PayloadAction<Partial<DonutChartConfig["centerTextStyle"]>>,
    ) => {
      state.currentConfig.centerTextStyle = {
        ...state.currentConfig.centerTextStyle,
        ...action.payload,
      };
    },
    updateColorPalette: (state, action: PayloadAction<string>) => {
      state.currentConfig.colorPalette = action.payload;

      if (action.payload === "Custom (per-slice)") {
        // Assign a distinct fallback color to slices that don't already have a
        // manual override, so the color pickers start from distinct colors.
        // manualColors is left untouched — it only gets entries when the user
        // explicitly picks a color, which drives the "dirty" state in the UI.
        const multiColors = [
          "#ffb09c",
          "#57dfbe",
          "#757679",
          "#a1a7df",
          "#c36179",
          "#57a68c",
          "#57bfe7",
          "#ebdd7c",
        ];
        state.currentConfig.series[0].data.forEach((item, index) => {
          if (!state.currentConfig.manualColors[item.name]) {
            item.color = multiColors[index % multiColors.length];
          }
        });
        return;
      }

      if (action.payload === "Multicolor") {
        // Update series colors based on palette - Multicolor uses the new color palette
        const multiColors = [
          "#ffb09c",
          "#57dfbe",
          "#757679",
          "#a1a7df",
          "#c36179",
          "#57a68c",
          "#57bfe7",
          "#ebdd7c",
        ];
        state.currentConfig.series[0].data.forEach((item, index) => {
          item.color = multiColors[index % multiColors.length];
        });
      } else if (action.payload === "Gradient") {
        // For gradient, interpolate between #019EDB and #00CF9C
        const startColor = "#019EDB";
        const endColor = "#00CF9C";
        const dataLength = state.currentConfig.series[0].data.length;

        state.currentConfig.series[0].data.forEach((item, index) => {
          // Calculate interpolation factor (0 to 1)
          const factor = dataLength > 1 ? index / (dataLength - 1) : 0;
          const interpolatedColor = interpolateColor(
            startColor,
            endColor,
            factor,
          );
          item.color = interpolatedColor;
        });
      } else if (action.payload === "Grayscale") {
        // For grayscale gradient, interpolate between light gray and dark gray
        const endColor = "#f0f0f0"; // Light gray
        const startColor = "#404040"; // Dark gray
        const dataLength = state.currentConfig.series[0].data.length;

        state.currentConfig.series[0].data.forEach((item, index) => {
          // Calculate interpolation factor (0 to 1)
          const factor = dataLength > 1 ? index / (dataLength - 1) : 0;
          const interpolatedColor = interpolateColor(
            startColor,
            endColor,
            factor,
          );
          item.color = interpolatedColor;
        });
      } else {
        // For other palettes, use multicolor as fallback
        const multiColors = [
          "#ffb09c",
          "#57dfbe",
          "#757679",
          "#a1a7df",
          "#c36179",
          "#57a68c",
          "#57bfe7",
          "#ebdd7c",
        ];
        state.currentConfig.series[0].data.forEach((item, index) => {
          item.color = multiColors[index % multiColors.length];
        });
      }
    },
    updateCustomColors: (state, action: PayloadAction<string[]>) => {
      state.currentConfig.customColors = action.payload;
      // Apply custom colors to data
      if (action.payload.length > 0) {
        state.currentConfig.series[0].data.forEach((item, index) => {
          item.color = action.payload[index % action.payload.length];
        });
      }
    },
    // Sets or clears (color: null) a single slice's manual color override.
    updateSliceManualColor: (
      state,
      action: PayloadAction<{ sliceName: string; color: string | null }>,
    ) => {
      if (action.payload.color === null) {
        delete state.currentConfig.manualColors[action.payload.sliceName];
      } else {
        state.currentConfig.manualColors[action.payload.sliceName] =
          action.payload.color;
      }
    },
    setAllSliceManualColors: (
      state,
      action: PayloadAction<{ sliceNames: string[]; color: string }>,
    ) => {
      action.payload.sliceNames.forEach((sliceName) => {
        state.currentConfig.manualColors[sliceName] = action.payload.color;
      });
    },
    updateTruncateCategoryText: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.truncateCategoryText = action.payload;
    },
    updatePercentageFormatting: (state, action: PayloadAction<number>) => {
      state.currentConfig.percentageFormatting = action.payload;
    },
    updateSubOnePercentStyle: (state, action: PayloadAction<"<1%" | "0%">) => {
      state.currentConfig.subOnePercentStyle = action.payload;
    },
    updateSliceValueFormat: (
      state,
      action: PayloadAction<"percentage" | "percentage_text">,
    ) => {
      state.currentConfig.sliceValueFormat = action.payload;
    },
    updateMinSliceTextThreshold: (state, action: PayloadAction<number>) => {
      state.currentConfig.minSliceTextThreshold = action.payload;
    },
    updateForceShowAllValues: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.forceShowAllValues = action.payload;
    },
    updateShowTooltip: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showTooltip = action.payload;
    },
    updateTooltipFormat: (state, action: PayloadAction<string>) => {
      state.currentConfig.tooltipFormat = action.payload;
    },
    // Enabling Show Slice N also enables Legend — the N line renders in the
    // legend row, so Legend is required and stays locked on until Show Slice N
    // is disabled again (enforced in the UI by disabling the Legend switch).
    updateShowSliceN: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showSliceN = action.payload;
      if (action.payload) {
        state.currentConfig.legendEnabled = true;
      }
    },
    updateShowChartN: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showChartN = action.payload;
    },
    updateQuestions: (state, action: PayloadAction<Questions | null>) => {
      state.question = action.payload;
      // Set default chart title to questions?.name when questions are updated
      if (action.payload?.name) {
        state.currentConfig.title = action.payload.name;
      }
      // Store question metadata in config for persistence
      if (action.payload) {
        state.currentConfig.questionLabel = action.payload.label;
        state.currentConfig.questionName = action.payload.name;
      }
    },
    updateChartMetadata: (
      state,
      action: PayloadAction<{
        datasetId: string;
        datasetName?: string;
        questionId: string;
        datasetVersionId?: number | null;
        activeFilters?: QuestionResponsesSearchFilter[];
      }>,
    ) => {
      state.datasetId = action.payload.datasetId;
      state.datasetName = action.payload.datasetName ?? null;
      state.questionId = action.payload.questionId;
      state.datasetVersionId = action.payload.datasetVersionId ?? null;
      state.activeFilters = action.payload.activeFilters ?? [];
    },
    updateFocusParams: (
      state,
      action: PayloadAction<{
        focusMode: string;
        focusEntity: string;
        focusEntityDisplayLabel?: string;
      } | null>,
    ) => {
      if (action.payload) {
        state.currentConfig.focusMode = action.payload.focusMode;
        state.currentConfig.focusEntity = action.payload.focusEntity;
        state.currentConfig.focusEntityDisplayLabel =
          action.payload.focusEntityDisplayLabel;
      } else {
        state.currentConfig.focusMode = undefined;
        state.currentConfig.focusEntity = undefined;
        state.currentConfig.focusEntityDisplayLabel = undefined;
      }
    },
    updateFocusRollup: (
      state,
      action: PayloadAction<{
        rollup: WidgetRollup;
        name?: string;
      } | null>,
    ) => {
      if (action.payload) {
        state.currentConfig.focusRollup = action.payload.rollup;
        state.currentConfig.focusRollupName = action.payload.name;
      } else {
        state.currentConfig.focusRollup = undefined;
        state.currentConfig.focusRollupName = undefined;
      }
    },
    updateChartData: (
      state,
      action: PayloadAction<{
        categories: string[];
        series: DonutChartConfig["series"];
        responsesMinDate?: string;
        responsesMaxDate?: string;
        chartN?: number | null;
        effectiveRollingPeriodCount?: number;
        rollingPeriodsWarningMessage?: string;
        comparisonRollingLabel?: string;
        missingRollingPeriodLabels?: string[];
      }>,
    ) => {
      state.currentConfig.categories = action.payload.categories;
      state.currentConfig.series = action.payload.series;
      if (action.payload.responsesMinDate) {
        state.currentConfig.responsesMinDate = action.payload.responsesMinDate;
      }
      if (action.payload.responsesMaxDate) {
        state.currentConfig.responsesMaxDate = action.payload.responsesMaxDate;
      }
      if (action.payload.chartN !== undefined) {
        state.currentConfig.chartN = action.payload.chartN;
      }
      state.currentConfig.effectiveRollingPeriodCount =
        action.payload.effectiveRollingPeriodCount;
      state.currentConfig.rollingPeriodsWarningMessage =
        action.payload.rollingPeriodsWarningMessage;
      state.currentConfig.comparisonRollingLabel =
        action.payload.comparisonRollingLabel;
      state.currentConfig.missingRollingPeriodLabels =
        action.payload.missingRollingPeriodLabels;
    },
    clearRollingAverageResponseFields: (state) => {
      state.currentConfig.effectiveRollingPeriodCount = undefined;
      state.currentConfig.rollingPeriodsWarningMessage = undefined;
      state.currentConfig.comparisonRollingLabel = undefined;
      state.currentConfig.missingRollingPeriodLabels = undefined;
    },
    updateRollingAverageEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.rollingAverage.enabled = action.payload;
      if (!action.payload) {
        state.currentConfig.rollingAverage.windowSize = 2;
      }
    },
    updateRollingAverageWindowSize: (state, action: PayloadAction<number>) => {
      state.currentConfig.rollingAverage.windowSize = action.payload;
      state.currentConfig.effectiveRollingPeriodCount = undefined;
      state.currentConfig.rollingPeriodsWarningMessage = undefined;
      state.currentConfig.comparisonRollingLabel = undefined;
      state.currentConfig.missingRollingPeriodLabels = undefined;
    },
    updateStatTestingEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.statisticalTesting.enabled = action.payload;
    },
    updateStatTestType: (state, action: PayloadAction<StatTestType>) => {
      state.currentConfig.statisticalTesting.testType = action.payload;
    },
    updateSignificanceLevel: (
      state,
      action: PayloadAction<SignificanceLevel>,
    ) => {
      state.currentConfig.statisticalTesting.significanceLevel = action.payload;
    },
    updateMinimumSampleSize: (state, action: PayloadAction<number>) => {
      state.currentConfig.statisticalTesting.minimumSampleSize = action.payload;
    },
    saveCurrentConfig: (state) => {
      const configToSave = { ...state.currentConfig };
      const tp = configToSave.timePeriodSelection?.timePeriod;
      if (tp && tp !== "all_time" && tp !== "custom_period") {
        configToSave.timePeriodSelection = {
          ...configToSave.timePeriodSelection,
          startDate: null,
          endDate: null,
        };
      }
      state.savedConfigs.push(configToSave);
    },
    loadConfig: (state, action: PayloadAction<DonutChartConfig>) => {
      Object.assign(state.currentConfig, {
        ...action.payload,
        visualizationBase:
          action.payload.visualizationBase ?? { ...DEFAULT_VISUALIZATION_BASE },
        timePeriodSelection: action.payload.timePeriodSelection ?? {
          timePeriod: "all_time",
          startDate: null,
          endDate: null,
        },
        viewerOptions: action.payload.viewerOptions ?? {
          ...DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
        },
        showSliceN: action.payload.showSliceN ?? false,
        showChartN: action.payload.showChartN ?? false,
        manualColors: action.payload.manualColors ?? {},
        truncateCategoryText: action.payload.truncateCategoryText ?? false,
        subOnePercentStyle: action.payload.subOnePercentStyle ?? "<1%",
        sliceCustomization: action.payload.sliceCustomization ?? {
          ...DEFAULT_SLICE_CUSTOMIZATION,
        },
        rollingAverage: action.payload.rollingAverage ?? {
          ...DEFAULT_ROLLING_AVERAGE,
        },
        statisticalTesting: action.payload.statisticalTesting ?? {
          ...DEFAULT_STATISTICAL_TESTING,
        },
      });
    },
    updateTimePeriod: (state, action: PayloadAction<TimePeriod>) => {
      state.currentConfig.timePeriodSelection.timePeriod = action.payload;
      if (action.payload !== "custom_period") {
        state.currentConfig.timePeriodSelection.startDate = null;
        state.currentConfig.timePeriodSelection.endDate = null;
      }
    },
    updateTimePeriodDates: (
      state,
      action: PayloadAction<{ startDate: string | null; endDate: string | null }>,
    ) => {
      state.currentConfig.timePeriodSelection.startDate = action.payload.startDate;
      state.currentConfig.timePeriodSelection.endDate = action.payload.endDate;
    },
    updateViewerOptions: (
      state,
      action: PayloadAction<PointInTimeViewerOptions>,
    ) => {
      state.currentConfig.viewerOptions = action.payload;
    },
    updateVisualizationBase: (
      state,
      action: PayloadAction<VisualizationBase>,
    ) => {
      state.currentConfig.visualizationBase = action.payload;
    },
    updateEmphasis: (state, action: PayloadAction<string>) => {
      state.currentConfig.emphasis = action.payload;
    },
    updateEmphasisDisplayFormat: (
      state,
      action: PayloadAction<"percentage_title" | "percentage_custom">,
    ) => {
      state.currentConfig.emphasisDisplayFormat = action.payload;
    },
    updateEmphasisCustomText: (state, action: PayloadAction<string>) => {
      state.currentConfig.emphasisCustomText = action.payload;
    },
    addGroup: (state) => {
      const newGroup = {
        id: `group-${Date.now()}`,
        name: "",
        sliceNames: [],
        showAsSlice: true,
        isHidden: false,
      };
      state.currentConfig.groups.push(newGroup);
    },
    // Rollups section (shared CategoryRollupsSection/CategoryRollupBuilder) —
    // Show as Slice defaults to enabled per PRD.
    addRollup: (
      state,
      action: PayloadAction<{
        id: string;
        name?: string;
        sourceCategoryLabels: string[];
      }>,
    ) => {
      state.currentConfig.groups.push({
        id: action.payload.id,
        name: action.payload.name ?? "",
        sliceNames: action.payload.sourceCategoryLabels,
        showAsSlice: true,
        isHidden: false,
      });
    },
    updateGroup: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<{
          name: string;
          sliceNames: string[];
          showAsSlice: boolean;
          isHidden: boolean;
        }>;
      }>,
    ) => {
      const group = state.currentConfig.groups.find(
        (g) => g.id === action.payload.id,
      );
      if (group) {
        Object.assign(group, action.payload.updates);

        // Force series data update to trigger chart re-render
        state.currentConfig.series = state.currentConfig.series.map(
          (series) => ({
            ...series,
            data: series.data.map((dataPoint) => ({ ...dataPoint })),
          }),
        );
      }
    },
    removeGroup: (state, action: PayloadAction<string>) => {
      state.currentConfig.groups = state.currentConfig.groups.filter(
        (g) => g.id !== action.payload,
      );
      // If removed group was emphasized, reset emphasis to none
      if (state.currentConfig.emphasis === `group:${action.payload}`) {
        state.currentConfig.emphasis = "none";
      }
    },
    toggleApplyFilters: (state) => {
      state.applyFilters = !state.applyFilters;
    },
    setApplyFilters: (state, action: PayloadAction<boolean>) => {
      state.applyFilters = action.payload;
    },
    resetToDefault: (state) => {
      Object.assign(state.currentConfig, defaultDonutChartConfig);
    },
  },
});

export const {
  togglePreviewMode,
  updateChartTitle,
  updateChartSubtitle,
  updateTitleBold,
  updateTitleUnderline,
  updateWeight,
  updateDataSorting,
  updateSliceLabelOverride,
  updateSlicePin,
  updateSliceCustomOrder,
  clearRollingAverageResponseFields,
  updateRollingAverageEnabled,
  updateRollingAverageWindowSize,
  updateStatTestingEnabled,
  updateStatTestType,
  updateSignificanceLevel,
  updateMinimumSampleSize,
  updateInnerRadius,
  updateOuterRadius,
  updateStartAngle,
  updateDataLabelsEnabled,
  updateDataLabelsPosition,
  updateDataLabelsFormat,
  updateDataLabelsDistance,
  updateLegendEnabled,
  updateAllowPointSelect,
  updateSliceOffset,
  updateCenterText,
  updateCenterTextEnabled,
  updateCenterTextStyle,
  updateColorPalette,
  updateCustomColors,
  updateSliceManualColor,
  setAllSliceManualColors,
  updateTruncateCategoryText,
  updatePercentageFormatting,
  updateSubOnePercentStyle,
  updateSliceValueFormat,
  updateMinSliceTextThreshold,
  updateForceShowAllValues,
  updateShowTooltip,
  updateTooltipFormat,
  updateShowSliceN,
  updateShowChartN,
  updateQuestions,
  updateChartMetadata,
  updateChartData,
  toggleApplyFilters,
  setApplyFilters,
  saveCurrentConfig,
  loadConfig,
  updateTimePeriod,
  updateTimePeriodDates,
  updateViewerOptions,
  updateVisualizationBase,
  updateEmphasis,
  updateEmphasisDisplayFormat,
  updateEmphasisCustomText,
  addGroup,
  addRollup,
  updateGroup,
  removeGroup,
  resetToDefault,
} = donutChartConfigSlice.actions;

export const donutChartConfigDispatcher = createDispatchers(
  donutChartConfigSlice.actions,
);
export const donutChartConfigSelector = (state: RootState) =>
  state.donutChartConfig;

export const donutChartConfigReducer = donutChartConfigSlice.reducer;
